import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { getSupabaseClient } from '../../data/supabaseClient'
import { loadAccess, type AccessResult } from '../../data/repositories/accessRepository'
import { SessionContext, type SessionContextValue } from './sessionContext'

/**
 * Authentication and organisation context.
 *
 * Two things happen here and the order matters. First the Supabase session is
 * resolved, which decides whether anyone is signed in. Only then is access
 * loaded from the database, which decides what they may reach.
 *
 * Both are held behind an explicit `status`, and nothing protected renders
 * until that status is settled. The alternative, rendering optimistically and
 * correcting afterwards, briefly shows a signed-out user the shape of the
 * application and briefly shows a viewer the administration link. Neither is a
 * data breach, but both teach the user something about a system they have no
 * business seeing, and both make the guards' behaviour depend on timing.
 *
 * Three rules this file will not bend:
 *
 *   1. **Nothing is read from localStorage as authorisation.** supabase-js keeps
 *      a session there, and it is a cache of a token, not evidence of anything.
 *      Authority comes from the verified session and from the database rows RLS
 *      allows through.
 *
 *   2. **No token is ever logged, returned or put in an error.** Not the access
 *      token, not the refresh token, not the session object.
 *
 *   3. **Organisation context is derived, never accepted.** There is no setter
 *      for role, organisation or financial access, so no component can hand one
 *      in from a URL, a form or storage.
 */


const SIGNED_OUT: Omit<SessionContextValue, 'signOut' | 'refreshAccess'> = {
  status: 'signed-out',
  userId: null,
  email: null,
  fullName: null,
  access: null,
  membership: null,
  role: null,
  canViewFinancials: false,
  linkedEmployeeId: null,
  isPlatformAdmin: false,
  organisationSuspended: false,
  hasMembership: false,
  accessFailure: null,
}

function derive(userId: string, result: AccessResult): Omit<SessionContextValue, 'signOut' | 'refreshAccess'> {
  if (result.kind !== 'ok') {
    return {
      ...SIGNED_OUT,
      status: 'access-error',
      userId,
      accessFailure:
        result.kind === 'denied'
          ? 'denied'
          : result.kind === 'incomplete'
            ? 'incomplete'
            : result.reason,
    }
  }

  const { access } = result
  // One membership for now. The shape is a list so that adding a switcher later
  // is a UI change rather than a rewrite of every consumer.
  const membership = access.memberships[0] ?? null

  return {
    status: 'ready',
    userId: access.profile.userId,
    email: access.profile.email,
    fullName: access.profile.fullName,
    access,
    membership,
    role: membership?.role ?? null,
    // Owners and admins hold financial access implicitly; the flag only ever
    // elevates a viewer (ADR-003). Mirrored from the database, never set here.
    canViewFinancials:
      membership !== null &&
      (membership.role === 'owner' || membership.role === 'admin' || membership.canViewFinancials),
    // Mirrored from the membership row, exactly as the role is. Never inferred,
    // and never matched from an email address (ADR-109).
    linkedEmployeeId: membership?.employeeId ?? null,
    isPlatformAdmin: access.isPlatformAdmin,
    organisationSuspended: membership?.organisationStatus === 'suspended',
    hasMembership: membership !== null,
    accessFailure: null,
  }
}

export default function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Omit<SessionContextValue, 'signOut' | 'refreshAccess'>>({
    ...SIGNED_OUT,
    status: 'loading',
  })

  // Guards against a slow access load landing after the user has signed out, or
  // after a newer load has already resolved. Without this, signing out during a
  // pending request can repopulate the context a moment later.
  const generation = useRef(0)
  const mounted = useRef(true)

  const resolveFor = useCallback(async (userId: string) => {
    const ticket = ++generation.current
    let result: AccessResult
    try {
      result = await loadAccess(userId)
    } catch {
      // Deliberately not logged. A thrown client error can carry request
      // details, and there is nothing here worth the risk of printing them.
      result = { kind: 'error', reason: 'unknown' }
    }
    if (!mounted.current || ticket !== generation.current) return
    setState(derive(userId, result))
  }, [])

  const clear = useCallback(() => {
    generation.current += 1
    if (mounted.current) setState({ ...SIGNED_OUT })
  }, [])

  useEffect(() => {
    mounted.current = true
    const supabase = getSupabaseClient()

    // getSession() reads the persisted session and refreshes it if needed, so
    // this is what makes a browser refresh keep you signed in.
    void supabase.auth
      .getSession()
      .then(({ data }) => {
        const userId = data.session?.user?.id
        if (typeof userId === 'string' && userId !== '') {
          void resolveFor(userId)
        } else {
          clear()
        }
      })
      .catch(() => {
        // An unreadable or corrupt stored session is treated as signed out
        // rather than as an error, because there is nothing to recover and
        // nothing the user can do except sign in again.
        clear()
      })

    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      const userId = session?.user?.id

      if (event === 'SIGNED_OUT' || typeof userId !== 'string' || userId === '') {
        clear()
        return
      }

      if (event === 'TOKEN_REFRESHED') {
        // The token changed, the identity did not. Reloading access here would
        // flicker the whole application every hour for no benefit.
        return
      }

      void resolveFor(userId)
    })

    return () => {
      mounted.current = false
      subscription.subscription.unsubscribe()
    }
  }, [resolveFor, clear])

  const signOut = useCallback(async () => {
    // Context is cleared first and unconditionally. If the network call fails,
    // the session is gone locally either way, and leaving protected content on
    // screen because a request failed would be the worse outcome.
    clear()
    try {
      await getSupabaseClient().auth.signOut()
    } catch {
      // Nothing to surface: the local session is already discarded.
    }
  }, [clear])

  const refreshAccess = useCallback(async () => {
    // The session is re-read rather than taken from state. Accepting an
    // invitation is exactly the case that breaks the cached version: the code
    // verification creates a session, the provider resolves access from it and
    // correctly finds no membership, and only then does acceptance create one.
    // Refreshing against a userId that was null a moment ago would silently do
    // nothing and leave the new member looking at the no-membership page.
    if (state.userId !== null) {
      await resolveFor(state.userId)
      return
    }
    try {
      const { data } = await getSupabaseClient().auth.getSession()
      const userId = data.session?.user?.id
      if (typeof userId === 'string' && userId !== '') await resolveFor(userId)
    } catch {
      // Nothing to refresh against. The caller decides what to show.
    }
  }, [state.userId, resolveFor])

  const value = useMemo<SessionContextValue>(
    () => ({ ...state, signOut, refreshAccess }),
    [state, signOut, refreshAccess],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}
